import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { ucfirst } from "../../Helpers/StringHelper";

/**
 * CharacterCounter component that displays a character counter and handles character input.
 *
 * @param {Object} props - Component props.
 * @param {string} props.value - The current value of the input field.
 * @param {number} [props.max=100] - The maximum number of characters allowed.
 * @param {function} props.onChange - The callback function to handle input changes.
 * @param {string} [props.className=""] - Additional CSS class name for the component.
 * @param {string} props.errorMessage - The error message to display.
 * @returns {JSX.Element|null} - Rendered component or null if an invalid child element is passed.
 */
const CharacterCounter = (props) => {
    const { value, max, children, onChange, className, errorMessage } = props;

    const [currentValue, setCurrentValue] = useState(value);
    const [counterClass, setCounterClass] = useState('');
    const counterMsg = `${currentValue.length}/${max}`;

    useEffect(() => {
        setCurrentValue(value);
        setCounterClass(currentValue.length >= max ? "error" : "");
    }, [value, max]);

    /**
     * Handles the onChange event of the input or textarea field.
     *
     * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e
     */
    const handleOnChange = (e) => {
        onChange(e);
        setCurrentValue(e.target.value);
    }

    let clonedChild = null;
    if(!React.isValidElement(children)) {
        return null;
    }

    const allowedTypes = ["textarea", "input", "SlugInput"];
    let tagName = typeof children.type === "function"
        ? children.type.displayName
        : children.type;

    if (!allowedTypes.includes(tagName)) {
        console.warn(
            `CharacterCounter component expects an element of one of the following as it's child: ${allowedTypes.join(', ')}. ${tagName} was passed.`
        );
        return null;
    }

    clonedChild = React.cloneElement(children, {
        onChange: handleOnChange,
        value: currentValue,
    });

    const getClassName = () => {
        let classNames = className;

        if (errorMessage) {
            classNames += ' error';
        }

        return classNames;
    }

    return (
        <div className={`character-counter-container ${getClassName()}`}>
            {clonedChild}

            <div className={`character-counter ${counterClass}`}>
                {counterMsg}
            </div>

            {(errorMessage) && (
                <small className="error">
                    {ucfirst(errorMessage)}
                </small>
            )}
        </div>
    )
}

CharacterCounter.propTypes = {
    value: PropTypes.string.isRequired,
    max: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string,
    errorMessage: PropTypes.string,
};

CharacterCounter.defaultProps = {
    max: 100,
    className: "",
};

export default CharacterCounter;
